import type {
  HttpDelete,
  HttpDeleteWithMeta,
  HttpGet,
  HttpGetBlob,
  HttpGetWithMeta,
  HttpPatch,
  HttpPatchFormData,
  HttpPatchMultipart,
  HttpPatchWithMeta,
  HttpPost,
  HttpPostFormData,
  HttpPostMultipart,
  HttpPostWithMeta,
  HttpPut,
  HttpRequest,
} from './http-client'
import type { RecoverSession } from '@/shared/domain'
import { NetworkError, SessionExpiredError } from '@/shared/domain'
import { env } from '@/shared/config/env'
import { HttpError } from './http-client'
import { deleteInFlightGet, getInFlightGet, setInFlightGet } from './in-flight-requests-store'
import { isTransportFailure } from './is-transport-failure'
import { recoverSessionOnce } from './session-recovery-store'

const CLIENT_PLATFORM = 'cc_web'
const UNAUTHORIZED = 401

type BuildRequestInit = (token: string | null) => RequestInit

type MetaResult<R> = { data: R; meta?: Record<string, unknown> }

export class SupabaseEdgeHttpClient
  implements
    HttpGet,
    HttpPost,
    HttpPut,
    HttpPatch,
    HttpPostFormData,
    HttpPatchFormData,
    HttpPostMultipart,
    HttpPatchMultipart,
    HttpPostWithMeta,
    HttpPatchWithMeta,
    HttpDelete,
    HttpDeleteWithMeta,
    HttpGetWithMeta,
    HttpGetBlob
{
  constructor(
    private readonly baseUrl: string,
    private readonly getAuthToken: () => Promise<string | null>,
    private readonly onRecoverSession?: RecoverSession,
  ) {}

  async get<R>(request: HttpRequest): Promise<R> {
    const fullUrl = this.buildUrl(request.url)
    const token = await this.getAuthToken()
    const buildInit: BuildRequestInit = (current) => {
      const init: RequestInit = {
        method: 'GET',
        headers: this.jsonHeaders(current),
        cache: 'no-store',
      }
      if (request.signal) init.signal = request.signal
      return init
    }

    if (request.signal) {
      const response = await this.send(fullUrl, buildInit, token)
      return this.handleResponse<R>(response, request.url, 'GET')
    }

    const dedupeKey = `${token ?? 'anon'}\n${fullUrl}`
    const existing = getInFlightGet(dedupeKey) as Promise<R> | undefined
    if (existing) return existing

    const promise = (async (): Promise<R> => {
      const response = await this.send(fullUrl, buildInit, token)
      return this.handleResponse<R>(response, request.url, 'GET')
    })().finally(() => deleteInFlightGet(dedupeKey))

    setInFlightGet(dedupeKey, promise)
    return promise
  }

  async post<B, R>(request: HttpRequest<B>): Promise<R> {
    const response = await this.sendJson(request.url, 'POST', request.body)
    return this.handleResponse<R>(response, request.url, 'POST')
  }

  async postWithMeta<B, R>(request: HttpRequest<B>): Promise<MetaResult<R>> {
    const response = await this.sendJson(request.url, 'POST', request.body)
    return this.parseWithMeta<R>(response, request.url, 'POST')
  }

  async put<B, R>(request: HttpRequest<B>): Promise<R> {
    const response = await this.sendJson(request.url, 'PUT', request.body)
    return this.handleResponse<R>(response, request.url, 'PUT')
  }

  async patch<B, R>(request: HttpRequest<B>): Promise<R> {
    const response = await this.sendJson(request.url, 'PATCH', request.body)
    return this.handleResponse<R>(response, request.url, 'PATCH')
  }

  async patchWithMeta<B, R>(request: HttpRequest<B>): Promise<MetaResult<R>> {
    const response = await this.sendJson(request.url, 'PATCH', request.body)
    return this.parseWithMeta<R>(response, request.url, 'PATCH')
  }

  async postFormData<R>(request: HttpRequest<FormData>): Promise<R> {
    const response = await this.sendForm(request.url, 'POST', request.body as FormData)
    return this.handleResponse<R>(response, request.url, 'POST')
  }

  async patchFormData<R>(request: HttpRequest<FormData>): Promise<R> {
    const response = await this.sendForm(request.url, 'PATCH', request.body as FormData)
    return this.handleResponse<R>(response, request.url, 'PATCH')
  }

  async postMultipart<R>(req: {
    url: string
    form: FormData
    headers?: Record<string, string>
  }): Promise<R> {
    const response = await this.sendForm(req.url, 'POST', req.form, req.headers)
    return this.handleResponse<R>(response, req.url, 'POST')
  }

  async patchMultipart<R>(req: {
    url: string
    form: FormData
    headers?: Record<string, string>
  }): Promise<R> {
    const response = await this.sendForm(req.url, 'PATCH', req.form, req.headers)
    return this.handleResponse<R>(response, req.url, 'PATCH')
  }

  async delete<R>(request: HttpRequest): Promise<R> {
    const response = await this.sendDelete(request)
    return this.handleResponse<R>(response, request.url, 'DELETE')
  }

  async deleteWithMeta<R>(request: HttpRequest): Promise<MetaResult<R>> {
    const response = await this.sendDelete(request)
    return this.parseWithMeta<R>(response, request.url, 'DELETE')
  }

  async getWithMeta<R>(request: HttpRequest<never>): Promise<MetaResult<R>> {
    const response = await this.sendWithToken(request.url, (token) => ({
      method: 'GET',
      headers: this.jsonHeaders(token),
    }))
    return this.parseWithMeta<R>(response, request.url, 'GET')
  }

  async getBlob(request: HttpRequest): Promise<Blob> {
    const response = await this.sendWithToken(request.url, (token) => ({
      method: 'GET',
      headers: this.jsonHeaders(token),
    }))
    if (!response.ok) {
      throw new HttpError(response.status, await this.safeParseJson(response), request.url, 'GET')
    }
    return response.blob()
  }

  private sendJson(url: string, method: string, body: unknown): Promise<Response> {
    return this.sendWithToken(url, (token) => ({
      method,
      headers: this.jsonHeaders(token),
      body: JSON.stringify(body),
    }))
  }

  private sendForm(
    url: string,
    method: string,
    form: FormData,
    extraHeaders?: Record<string, string>,
  ): Promise<Response> {
    return this.sendWithToken(url, (token) => ({
      method,
      headers: { ...this.authOnlyHeaders(token), ...extraHeaders },
      body: form,
    }))
  }

  private sendDelete(request: HttpRequest): Promise<Response> {
    return this.sendWithToken(request.url, (token) => {
      const headers = this.jsonHeaders(token)
      if (request.body === undefined) {
        return { method: 'DELETE', headers }
      }
      return {
        method: 'DELETE',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify(request.body),
      }
    })
  }

  private async sendWithToken(url: string, buildInit: BuildRequestInit): Promise<Response> {
    return this.send(this.buildUrl(url), buildInit, await this.getAuthToken())
  }

  private async send(
    fullUrl: string,
    buildInit: BuildRequestInit,
    token: string | null,
  ): Promise<Response> {
    const response = await this.fetchOrRaiseTransportError(fullUrl, buildInit(token))
    if (response.status !== UNAUTHORIZED || !this.onRecoverSession) return response

    const recoveredToken = await recoverSessionOnce(this.onRecoverSession)
    if (recoveredToken === null || recoveredToken === token) throw new SessionExpiredError()

    const retried = await this.fetchOrRaiseTransportError(fullUrl, buildInit(recoveredToken))
    if (retried.status === UNAUTHORIZED) throw new SessionExpiredError()
    return retried
  }

  private async fetchOrRaiseTransportError(url: string, init: RequestInit): Promise<Response> {
    try {
      return await fetch(url, init)
    } catch (err) {
      if (isTransportFailure(err)) throw new NetworkError()
      throw err
    }
  }

  private buildUrl(url: string): string {
    return `${this.baseUrl.replace(/\/$/, '')}${url}`
  }

  private platformHeaders(): Record<string, string> {
    return {
      'X-BB-Client': CLIENT_PLATFORM,
      'X-BB-App-Version': env.appVersion,
    }
  }

  private jsonHeaders(token: string | null): Record<string, string> {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      ...this.platformHeaders(),
    }
    if (token) {
      headers['Authorization'] = `Bearer ${token}`
    }
    return headers
  }

  private authOnlyHeaders(token: string | null): Record<string, string> {
    const headers: Record<string, string> = { ...this.platformHeaders() }
    if (token) {
      headers['Authorization'] = `Bearer ${token}`
    }
    return headers
  }

  private async handleResponse<R>(
    response: Response,
    requestUrl?: string,
    method?: string,
  ): Promise<R> {
    if (!response.ok) {
      throw new HttpError(response.status, await this.safeParseJson(response), requestUrl, method)
    }
    if (response.status === 204) {
      return undefined as R
    }
    const body = await response.json()
    if (body !== null && typeof body === 'object' && 'ok' in body) {
      if (body.ok === false) {
        throw new Error(body.error ?? 'Request failed')
      }
      if (body.ok === true) {
        if ('data' in body) {
          return body.data as R
        }
        return undefined as R
      }
    }
    return body as R
  }

  private async parseWithMeta<R>(
    response: Response,
    requestUrl: string,
    method: string,
  ): Promise<MetaResult<R>> {
    if (!response.ok) {
      throw new HttpError(response.status, await this.safeParseJson(response), requestUrl, method)
    }
    if (response.status === 204) {
      return { data: undefined as R, meta: undefined }
    }
    const body = await response.json()
    if (body !== null && typeof body === 'object' && 'ok' in body) {
      if (body.ok === false) {
        throw new Error(body.error ?? 'Request failed')
      }
      if (body.ok === true) {
        return {
          data: ('data' in body ? body.data : undefined) as R,
          meta: body.meta as Record<string, unknown> | undefined,
        }
      }
    }
    return { data: body as R, meta: undefined }
  }

  private async safeParseJson(response: Response): Promise<unknown> {
    try {
      return await response.json()
    } catch {
      return undefined
    }
  }
}
